import { useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Radio, RadioGroup, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useNow } from '../../inbox/components/SlaBadge';
import { CreateTicketModal } from '../components/CreateTicketModal';
import { PriorityTag, TicketSlaBadge } from '../components/TicketBits';
import { BOARD_CATEGORIES, decideDrop } from '../lib/board';
import { TICKETS_KEY, categoryLabel, swatchColour, ticketErrorText, useLookups } from '../lib/data';
import '../tickets.css';

const PER_COLUMN = 50;
const DRAG_TYPE = 'application/x-rr-ticket'; // i18n-ignore: drag data type

interface PendingMove { category: tickets.StatusCategory; name: string }
interface Choice { ticket: tickets.TicketSummary; options: tickets.Status[]; picked: string }

export default function TicketsBoardPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const now = useNow(30_000);
  const lk = useLookups();

  const [view, setView] = usePersistedState<tickets.TicketView>('tenant.tickets.board.view', 'all');
  const [typeId, setTypeId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [over, setOver] = useState<tickets.StatusCategory | null>(null);
  const [moves, setMoves] = useState<Record<string, PendingMove>>({});
  const [choice, setChoice] = useState<Choice | null>(null);
  const [creating, setCreating] = useState(false);

  const columns = useQueries({
    queries: BOARD_CATEGORIES.map((category) => ({
      queryKey: [TICKETS_KEY, 'board', view, typeId, teamId, category],
      queryFn: () => tickets.listTickets({ view, type_id: typeId || undefined, team_id: teamId || undefined, category, limit: PER_COLUMN }),
      refetchInterval: 60_000
    }))
  });

  const all = columns.flatMap((c) => c.data?.data ?? []);
  const byId = new Map(all.map((x) => [x.id, x]));
  const failed = columns.find((c) => c.error);

  async function move(ticket: tickets.TicketSummary, status: tickets.Status) {
    setMoves((m) => ({ ...m, [ticket.id]: { category: status.category, name: status.name } }));
    try {
      await tickets.updateTicket(ticket.id, { status_id: status.id });
      notifySuccess(t('tickets.board.moved', { number: ticket.number, status: status.name }), t('tickets.board.title'));
      await qc.invalidateQueries({ queryKey: [TICKETS_KEY] });
    } catch (e) {
      notifyError(ticketErrorText(t, e), t('tickets.board.title'));
    } finally {
      setMoves((m) => {
        const next = { ...m };
        delete next[ticket.id];
        return next;
      });
    }
  }

  function onDrop(e: DragEvent<HTMLElement>, category: tickets.StatusCategory) {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData(DRAG_TYPE) || e.dataTransfer.getData('text/plain');
    const ticket = byId.get(id);
    if (!ticket || moves[id]) return;
    const d = decideDrop(lk.statuses, ticket, category);
    if (d.kind === 'same') return;
    if (d.kind === 'none') {
      notifyError(t('tickets.board.noStatus', { type: ticket.type.name, category: categoryLabel(t, category) }), t('tickets.board.title'));
      return;
    }
    if (d.kind === 'one') { void move(ticket, d.status); return; }
    setChoice({ ticket, options: d.options, picked: d.options[0]!.id });
  }

  // Where each ticket is drawn: in the column it is moving to while the change is on its way.
  const shown = (category: tickets.StatusCategory) =>
    all.filter((x) => (moves[x.id]?.category ?? x.status.category) === category);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('tickets.board.title')}
        description={t('tickets.board.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="tickets" />
            <Button size="sm" variant="default" onClick={() => nav('/tickets')}>{t('tickets.queue.title')}</Button>
            <Button size="sm" onClick={() => setCreating(true)}>{t('tickets.create.title')}</Button>
          </Group>
        )}
      />

      <Group gap="sm" wrap="wrap" align="flex-end">
        <Select size="sm" aria-label={t('tickets.board.viewLabel')} value={view} onChange={(e) => setView(e.currentTarget.value as tickets.TicketView)}
          data={tickets.TICKET_VIEWS.map((v) => ({ value: v, label: t(`tickets.view.${v}`) }))} />
        <Select size="sm" aria-label={t('tickets.field.type')} value={typeId} onChange={(e) => setTypeId(e.currentTarget.value)}
          data={[{ value: '', label: t('tickets.filter.anyType') }, ...lk.types.map((x) => ({ value: x.id, label: x.name }))]} />
        {lk.teams.length > 0 && (
          <Select size="sm" aria-label={t('tickets.field.team')} value={teamId} onChange={(e) => setTeamId(e.currentTarget.value)}
            data={[{ value: '', label: t('tickets.filter.anyTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]} />
        )}
        <Text role="caption" c="var(--rr-color-text-muted)">{t('tickets.board.hint')}</Text>
      </Group>

      {failed?.error && (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{ticketErrorText(t, failed.error)}</span>
            <Button size="xs" variant="default" onClick={() => columns.forEach((c) => void c.refetch())}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <div className="rr-tickets__board" data-testid="tickets-board" data-guide="tickets-board">
        {BOARD_CATEGORIES.map((category, i) => {
          const col = columns[i]!;
          const cards = shown(category);
          const total = col.data?.meta?.counts?.[category];
          return (
            <section
              key={category}
              className={`rr-tickets__col${over === category ? ' is-over' : ''}`}
              aria-label={categoryLabel(t, category)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== category) setOver(category); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null); }}
              onDrop={(e) => onDrop(e, category)}
            >
              <header className={`rr-tickets__col-head is-${category}`}>
                <Text role="bodySmall" fw={600}>{categoryLabel(t, category)}</Text>
                <span className="rr-tickets__col-count">{formatCount(total ?? cards.length)}</span>
              </header>
              <div className="rr-tickets__col-body">
                {col.isPending && <div className="rr-tickets__card is-skeleton" aria-hidden />}
                {!col.isPending && cards.length === 0 && (
                  <Text role="caption" c="var(--rr-color-text-muted)" ta="center" className="rr-tickets__col-empty">{t('tickets.board.empty')}</Text>
                )}
                {cards.map((x) => (
                  <article
                    key={x.id}
                    className={`rr-tickets__card${moves[x.id] ? ' is-moving' : ''}`}
                    style={{ ['--rr-tk-colour' as string]: swatchColour(x.type.colour) }}
                    draggable={!moves[x.id]}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(DRAG_TYPE, x.id);
                      e.dataTransfer.setData('text/plain', x.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  >
                    <button type="button" className="rr-tickets__card-link" onClick={() => nav(`/tickets/${encodeURIComponent(x.id)}`)}
                      aria-label={t('tickets.board.open', { number: x.number, subject: x.subject })}>
                      <Group gap="xs" justify="space-between" wrap="nowrap">
                        <span className="rr-tickets__card-number">{x.number}</span>
                        <PriorityTag priority={x.priority} />
                      </Group>
                      <span className="rr-tickets__card-subject">{x.subject}</span>
                      <span className="rr-tickets__card-meta">
                        {x.contact?.name || x.contact?.ref || t('tickets.noClient')} · {x.type.name}
                      </span>
                    </button>
                    <Group gap="xs" justify="space-between" wrap="nowrap" className="rr-tickets__card-foot">
                      <Text role="caption" c="var(--rr-color-text-muted)" lineClamp={1}>
                        {moves[x.id]
                          ? t('tickets.board.moving', { status: moves[x.id]!.name })
                          : x.assignee_user_id
                            ? (x.assignee_user_id === user?.id ? t('tickets.me') : lk.personLabel(x.assignee_user_id) || t('common.unknown'))
                            : t('tickets.unassigned')}
                      </Text>
                      <TicketSlaBadge input={x} now={now} />
                    </Group>
                  </article>
                ))}
                {col.data?.meta?.next_cursor && (
                  <Text role="caption" c="var(--rr-color-text-muted)" ta="center">{t('tickets.board.more', { n: PER_COLUMN })}</Text>
                )}
              </div>
            </section>
          );
        })}
      </div>

      <Modal opened={!!choice} onClose={() => setChoice(null)} title={t('tickets.board.chooseTitle')} size="sm">
        {choice && (
          <Stack gap="md">
            <Text role="bodySmall">{t('tickets.board.chooseBody', { number: choice.ticket.number, type: choice.ticket.type.name })}</Text>
            <RadioGroup value={choice.picked} onChange={(v) => setChoice({ ...choice, picked: v })} name="board-status">
              <Stack gap="xs">
                {choice.options.map((s) => <Radio key={s.id} value={s.id} label={s.name} description={s.client_label ?? undefined} />)}
              </Stack>
            </RadioGroup>
            <Group gap="sm" justify="flex-end">
              <Button variant="default" onClick={() => setChoice(null)}>{t('action.cancel')}</Button>
              <Button onClick={() => {
                const s = choice.options.find((o) => o.id === choice.picked);
                const tk = choice.ticket;
                setChoice(null);
                if (s) void move(tk, s);
              }}>{t('tickets.board.moveHere')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <CreateTicketModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(tk) => notifySuccess(t('tickets.create.done', { number: tk.number }), t('tickets.title'))}
      />
    </Stack>
  );
}

